import { stackedTranslationEnabledValues } from './constants'

/**
 * Sorts the keys array based on the order specified in the sortKeyOrder array.
 *
 * @param {Array<string>} sortKeyOrder - An array of keys specifying the desired order.
 * @param {Array<string>} keys - An array of keys to be sorted.
 * @returns {Array<string>} - A new array of keys sorted according to the sortKeyOrder.
 */
const sortKeys = (sortKeyOrder = [], keys = []) => {
  return sortKeyOrder
    .map((sortKey) => keys.find((key) => sortKey === key))
    .filter((x) => x)
}

const isStackedTranslationEnabled = (stackedTranslation, isPreview) => {
  return isPreview
    ? [...stackedTranslationEnabledValues, undefined].includes(
        stackedTranslation
      )
    : stackedTranslationEnabledValues.includes(stackedTranslation)
}

function isEqualNodeIgnoreAttrs(a, b, ignoreAttrs) {
  if (a.tagName !== b.tagName) return false
  if (a.childNodes.length !== b.childNodes.length) return false

  // Compare attributes, ignoring ones in ignoreAttrs
  const attrsA = [...a.attributes].filter(
    (attr) => !ignoreAttrs.includes(attr.name)
  )
  const attrsB = [...b.attributes].filter(
    (attr) => !ignoreAttrs.includes(attr.name)
  )

  if (attrsA.length !== attrsB.length) return false

  const sortByName = (x: Attr, y: Attr) => x.name.localeCompare(y.name)
  attrsA.sort(sortByName)
  attrsB.sort(sortByName)

  for (let i = 0; i < attrsA.length; i++) {
    if (
      attrsA[i].name !== attrsB[i].name ||
      attrsA[i].value !== attrsB[i].value
    )
      return false
  }

  // Compare children recursively
  for (let i = 0; i < a.childNodes.length; i++) {
    const childA = a.childNodes[i]
    const childB = b.childNodes[i]

    if (childA.nodeType !== childB.nodeType) return false

    if (childA.nodeType === Node.TEXT_NODE) {
      if (childA.textContent?.trim() !== childB.textContent?.trim())
        return false
    } else if (
      childA.nodeType === Node.ELEMENT_NODE &&
      !isEqualNodeIgnoreAttrs(childA, childB, ignoreAttrs)
    ) {
      return false
    }
  }

  return true
}

/**
 * Compares two HTML fragments semantically.
 * - Considers ignoreAttributes which are to be ignored during comparison
 * @param {string} html1 - The first HTML fragment string
 * @param {string} html2 - The second HTML fragment string
 * @returns {boolean} True if the HTML structures are semantically equal
 */
const areHtmlFragmentsEqual = (html1, html2) => {
  // Quick check: handle empty/null and exact string match
  html1 = html1 ? html1.trim() : ''
  html2 = html2 ? html2.trim() : ''
  if (html1 === html2) return true

  const parser = new DOMParser()
  const docA = parser.parseFromString(html1, 'text/html')
  const docB = parser.parseFromString(html2, 'text/html')

  const nodeA = docA.body.firstElementChild
  const nodeB = docB.body.firstElementChild

  if (!nodeA || !nodeB) return false

  const ignoreAttributes = ['data-pa-id']
  return isEqualNodeIgnoreAttrs(nodeA, nodeB, ignoreAttributes)
}

const removeDuplicateStimuli = (stimuliList, getStimulus) => {
  if (!stimuliList || stimuliList.length < 2) {
    return stimuliList
  }

  const firstStimulus = getStimulus(stimuliList[0])
  const secondStimulus = getStimulus(stimuliList[1])

  const isIdentical = areHtmlFragmentsEqual(firstStimulus, secondStimulus)

  if (isIdentical) {
    return [stimuliList[0]]
  }
  return stimuliList
}

export {
  sortKeys,
  isStackedTranslationEnabled,
  removeDuplicateStimuli,
  areHtmlFragmentsEqual,
}
